import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import './editor-main.scss';
import Description from './description/description';
import Documents from './documents/documents';
import Surveys from './surveys/surveys';
import Products from './products/products';
import Questions from './questions';
import Quizzes from './quizzes';
import SimilarSessions from './similar-sessions/similar-sessions';
import Slideshows from './slideshows';
import Speakers from './speakers/speakers';
import Video from './video';
import {
	PageModuleGroupModules,
	PageModuleType,
	SessionTabsLayoutTypes,
	NavMainItems,
	EPreviewTypes,
	ModuleGroups,
	TranslateString
} from '../../../../../types/working-model';
import { setActiveSessionGroupModule } from '../../../../../store/actions/admin/create-event/session';
import BlankSection from '../../homepage/editor/blank-section';
import Sticky from '../../../../general-ui/sticky/sticky';
import { useTypedSelector } from '../../../../../store/reducers/use-typed-selector';
import Icon from '../../../../general-ui/icon';
import RenderModules from './render-modules';
import useTransformGroupModules from '../../../../../utils/use-transform-group-modules';
import { TemplateClassNames } from '../../../../../types/template-layouts';
import SessionHeader from '../../../../live-event/session/session-modules/header/header';
import { PostRegisterSideBar } from '../../homepage/post-register-preview/main-preview';
import { GetDefaultMainNavItems } from '../../../../../store/utils/create-event';
import LiveChat from '../../../../live-event/session/live-chat/live-chat';
import { useParams } from 'react-router';
import { resetScrollState } from '../../../../../store/actions/admin/event-panel';
import SessionEditorHeader from './session-editor-header';
import SessionPreviewModal from '../../homepage/editor/preview/session-preview';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

// for testing only:
// import VideoPlayer from '../../../../video-player/video-player';
// import Stream from '../../../../video-player/stream';

interface ModuleMapType {
	[key: number]: any;
}

function Dummy(): JSX.Element {
	return <div></div>;
}

//Maps the page module type to the function for the component. 
export const ModuleMap: ModuleMapType = {
	[PageModuleType.main_event_banner]: Dummy,
	[PageModuleType.speakers]: Speakers,
	[PageModuleType.agenda]: Dummy,
	[PageModuleType.about]: Dummy,
	[PageModuleType.videos]: Dummy,
	[PageModuleType.callout]: Dummy,
	[PageModuleType.sponsors]: Dummy,
	[PageModuleType.broadcast]: Dummy,//dead module type, but don't remove from the enumerator or things will break
	[PageModuleType.questions]: Questions,
	[PageModuleType.quizzes]: Quizzes,
	[PageModuleType.survey]: Surveys,
	[PageModuleType.products]: Products,
	[PageModuleType.slideshows]: Slideshows,
	[PageModuleType.documents]: Documents,
	[PageModuleType.similar_sessions]: SimilarSessions,
	[PageModuleType.feed]: Dummy,
	[PageModuleType.embed_widget]: BlankSection,
	[PageModuleType.description]: Description,
	[PageModuleType.blank]: BlankSection
};

export const ModuleMapNavButtons: ModuleMapType = {
	[PageModuleType.main_event_banner]: "",
	[PageModuleType.speakers]: "Speakers",
	[PageModuleType.agenda]: "",
	[PageModuleType.about]: "",
	[PageModuleType.videos]: "",
	[PageModuleType.callout]: "",
	[PageModuleType.sponsors]: "",
	[PageModuleType.broadcast]: "",
	[PageModuleType.questions]: "Questions",
	[PageModuleType.quizzes]: "Quizzes",
	[PageModuleType.survey]: "Surveys",
	[PageModuleType.products]: "Products",
	[PageModuleType.slideshows]: "Slideshows",
	[PageModuleType.documents]: "Documents",
	[PageModuleType.similar_sessions]: "Similar Sessions",
	[PageModuleType.feed]: "Feed",
	[PageModuleType.embed_widget]: "Embed Widget",
	[PageModuleType.description]: "Description",
	[PageModuleType.blank]: "Blank"
};

const Editor: React.FC = () => {
	const {
		CreateSessionReducer: {
			workingSession,
			selectedPreviewGroupUuid,
		},
		EventPanelReducer: {
			panelOpen,
			sessionEditorGroup,
		},
		CreateEventReducer: {
			workingEvent,
			editorSize,
			previewMode
		},
	} = useTypedSelector(state => state);

	const [isLoading, setIsLoading] = useState(false);
	const dispatch = useDispatch();

	const { t } = useTranslation("session");
	const { language }: any = useParams();


	const template = workingEvent?.template.name
		? TemplateClassNames[workingEvent!.template.name]
		: TemplateClassNames.Apollo;

	useTransformGroupModules();

	const sessionEditorRef = useRef<HTMLDivElement>(null);
	function scrollToEditor() {
		// The ref for this scroll behavior is the window itself, not any specific module
		// Commenting for now until we figure out a scroll behavior with tabs
		// if (sessionEditorRef.current) {
		// 	sessionEditorRef.current.scrollIntoView({
		// 		behavior: 'smooth',
		// 		block: 'start',
		// 	});
		// }
	}

	useEffect(() => {
		if (sessionEditorGroup) {
			dispatch(setActiveSessionGroupModule(sessionEditorGroup));
			setTimeout(() => scrollToEditor(), 100);
			setTimeout(() => dispatch(resetScrollState()), 1500);
		}
	}, [dispatch, sessionEditorGroup]);


	const layout = workingSession?.layout?.tabs?.layout || SessionTabsLayoutTypes.Horizontal;

	const showIcons = workingSession?.layout?.tabs?.show_icons ?? true;

	const groupedNavButtons = useMemo(() => {
		// create ordered lists for each group
		if (!workingSession?.module_grouping) return null;
		return workingSession?.module_grouping?.map((moduleGroup, index) => {
			const navigate = (moduleGroup: PageModuleGroupModules) => () => dispatch(setActiveSessionGroupModule(moduleGroup.uuid));

			if (!moduleGroup.is_on || !moduleGroup.modules.length) return null;
			const { uuid, icon, name } = moduleGroup;

			let _name: string;
			//Check to see if "name" is a Translate string, if so return the translated value, if not, then it is already a string
			const stringName = name as ModuleGroups;
			const translateName = name as TranslateString;
			if (typeof translateName === 'object') {
				// final fallback is for sessions created with a default language that was not one of the session languages (before that got fixed)
				_name = translateName[language] as string ?? translateName.base ?? Object.values(translateName)[0];
			} else {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				_name = t(stringName) ?? stringName;
			}

			return (
				<li
					key={uuid}
					className={classNames({
						active: selectedPreviewGroupUuid
							? uuid === selectedPreviewGroupUuid
							: index === 0
					})}
				>
					<button
						onClick={navigate(moduleGroup)}
						className={classNames(
							'clear no-style',
							{
								'sharp': [SessionTabsLayoutTypes.Horizontal, SessionTabsLayoutTypes.Vertical].includes(layout),
							}
						)}
					>
						<Icon name={icon || ""} size={16} color={classNames({ noIcon: !showIcons })} />
						<span>{_name}</span>
					</button>
				</li >
			);
		});
	}, [workingSession, dispatch, selectedPreviewGroupUuid, showIcons, layout, language, t]);

	const renderModulesByGroup = () => {
		const uuid = selectedPreviewGroupUuid || workingSession?.module_grouping?.[0].uuid;
		if (!uuid) return <></>;
		return <RenderModules groupUuid={uuid} />;
	};

	const renderAllModules = () => {
		return (
			<>
				{workingSession?.module_grouping?.map(group => (
					<RenderModules
						key={group.uuid}
						groupUuid={group.uuid}
					/>))}
			</>
		);
	};

	const renderContent = () => {

		//Vertical layout is only available on desktop, switch to horizontal view if vertical is chosen but viewed on mobile or tablet
		const _layout = (editorSize !== "desktop" && layout === SessionTabsLayoutTypes.Vertical)
			? SessionTabsLayoutTypes.Horizontal
			: layout;

		switch (_layout) {
			case SessionTabsLayoutTypes.Horizontal:
				return (
					<div className="page-container-horizontal">
						<div className="page-container-body">
							{renderModulesByGroup()}
						</div>
						<div className="page-container-horizontal-tabs-container">
							<ul className="page-container-horizontal-tabs tab-navigation">
								{groupedNavButtons}
							</ul>
						</div>
					</div>
				);
			case SessionTabsLayoutTypes.NoTabs:
				return (
					<div className={classNames("editor-modules page-container-body", { "page-container-horizontal": editorSize === 'mobile' })}>
						{renderAllModules()}
					</div>
				);
			default:
				return (
					<div className="page-container-vertical">
						<div>
							<Sticky
								top={workingSession && workingSession.languages?.length > 1 ? "70px" : '0px'}
								renderStickyElements={() => (
									<div className="editor-navigation">
										<ul className="tab-navigation" >
											{groupedNavButtons}
										</ul>
									</div>
								)}
							/>
						</div>
						<div className="editor-modules page-container-body">
							{renderModulesByGroup()}
						</div>
					</div>
				);
		}
	};
	const isDesktop = editorSize === 'desktop';

	const styleOverrides = useMemo(() => {
		// const sessionToUse = session ? session : workingSession;
		const backgroundColor = workingSession?.video?.customizations?.backgroundColor;
		// Apollo desktop has a gradient
		if (backgroundColor && template === TemplateClassNames.Apollo && isDesktop) {
			return { background: `linear-gradient(180deg, rgba(0, 0, 0, .3) 0%, rgba(var(--${backgroundColor}-rgb), 0)`, backgroundColor: `var(--${backgroundColor})` };
		}

		if (backgroundColor) {
			return { background: `var(--${backgroundColor})` };
		}

		return {};
	}, [workingSession, template, isDesktop]);


	const isSingleSession = workingEvent?.sessions?.length === 1;
	const hasHomepage = workingEvent?.homepage;

	return workingSession && workingEvent ? (
		<div
			className={classNames("session-editor editor-main-border")}
		>
			<SessionEditorHeader session={workingSession} setIsLoading={setIsLoading} />
			<div className={classNames("editor editor-main-container session", editorSize, { 'panel-open': !!panelOpen, loading: isLoading })}>
				{isDesktop && hasHomepage && (
					<PostRegisterSideBar
						template={template}
						eventMainNavItems={workingEvent?.homepage?.event_main_nav_items || GetDefaultMainNavItems(isSingleSession)}
						activeMenuItem={isSingleSession ? NavMainItems.Session : NavMainItems.Sessions}
						logo={workingEvent.homepage?.header.logo}
					/>)
				}
				<div className="editor-wrapper-right">

					<SessionHeader
						session={workingSession}
						eventBundle={workingEvent}
						isEditor
						editorSize={editorSize}
						styleOverrides={styleOverrides}
					/>
					{/* for testing only: */}
					{/* <div>
					<VideoPlayer streamUrl={workingSession?.ivs_channel?.channel?.playbackUrl} />
					</div>
					<div>
						<Stream />
					</div> */}
					<Video editorSize={editorSize} language={language} />
					{editorSize === 'mobile' && (
						<LiveChat
							session={workingSession}
							loading={false}
							language={language}
							registrationOn={!!workingEvent?.registration_on}
							blProfileUser={null}
							template={template}
							forceOverlay={false}
							mobileOnly={true}
							isEditor={true}
							chatClosed={false}
							onChatClose={() => ({})}
							eventBundle={workingEvent}
							isAdmin={true}
						/>
					)}
					<div ref={sessionEditorRef} className={classNames("editor-wrapper session-editor-wrapper", template)}>
						{renderContent()}
						<SessionPreviewModal open={previewMode === EPreviewTypes.Session} />
					</div>
				</div>
			</div>
		</div >
	) : null;
};

export default Editor;
